import { useState } from "react";

import styles from "./TaskView.module.css";
import { Button } from "../../components/Button";
import { retryTaskStart } from "../../data/api/tasks";
import { Icon } from "../../icons";

/**
 * "Retry" for a task whose sandbox never came up (`agents-and-tasks.md`
 * §Retrying a task that never started). The task goes back to `pending` and
 * the thread gets a new provisioning card; `onRetried` re-reads the task so
 * the header follows without waiting for `task.updated`.
 */
export const RetryStart = ({
  onRetried,
  taskId,
}: {
  readonly onRetried: () => void;
  readonly taskId: string;
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retry = (): void => {
    setBusy(true);
    setError(null);
    void retryTaskStart(taskId)
      .then(onRetried)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Request failed.");
      })
      .finally(() => setBusy(false));
  };
  return (
    <span className={styles.retryStart}>
      {/* `title` as well as the text: the line is clamped. */}
      {error ? (
        <span className={styles.actionError} role="alert" title={error}>
          {error}
        </span>
      ) : null}
      <Button
        disabled={busy}
        onClick={retry}
        size="sm"
        title="Provision a new sandbox and start the task again"
        variant="accent"
      >
        <Icon.Refresh size={12} /> {busy ? "Retrying…" : "Retry"}
      </Button>
    </span>
  );
};
